/** Sprint 5-b PWA · 會議紀錄檢視（摘要 + 決議 + action items + 逐字稿 + 列印）。 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Divider, List, ListItem,
  ListItemText, Paper, Stack, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintIcon from '@mui/icons-material/Print';

import { useMessage } from '../../../contexts/MessageContext';
import { getMinutes, Minutes } from './services/voiceCalendarService';

const MinutesPage: React.FC = () => {
  const { eventId } = useParams();
  const eid = Number(eventId);
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pwaMinutes.${k}`, d);
  const { showApiError } = useMessage();
  const [m, setM] = useState<Minutes | null>(null);

  useEffect(() => { getMinutes(eid).then(setM).catch(showApiError); }, [eid, showApiError]);

  if (!m) return <Box sx={{ p: 3 }}><Typography variant="body2" color="text.secondary">{tt('loading', '載入中…')}</Typography></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 680, mx: 'auto' }} className="pwa-minutes">
      <Stack direction="row" alignItems="center" justifyContent="space-between" className="no-print">
        <Button size="small" onClick={() => nav(`/ee_pwa_meetings/${eid}`)}>← {tt('back', '返回會議室')}</Button>
        <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
          {tt('print', '列印')}</Button>
      </Stack>
      <Typography variant="h6" sx={{ my: 1 }}>📝 {tt('title', '會議紀錄')}</Typography>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('summary', '摘要')}</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.summary || tt('none', '（無）')}</Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('decisions', '決議')}</Typography>
        {m.decisions.length === 0 ? <Typography variant="body2" color="text.secondary">{tt('none', '（無）')}</Typography>
          : <List dense disablePadding>{m.decisions.map((d, i) => (
            <ListItem key={i} disableGutters><ListItemText primary={`• ${d.content}`} /></ListItem>))}</List>}
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('actions', '待辦事項')}</Typography>
        {m.action_items.length === 0 ? <Typography variant="body2" color="text.secondary">{tt('none', '（無）')}</Typography>
          : <List dense disablePadding>{m.action_items.map((a) => (
            <ListItem key={a.id} disableGutters>
              <ListItemText primary={a.content}
                secondary={<Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                  {a.assignee_name && <Chip size="small" label={a.assignee_name}
                    color={a.assignee_user_id ? 'info' : 'default'} />}
                  {a.due && <Chip size="small" variant="outlined" label={a.due} />}
                </Stack>} />
            </ListItem>))}</List>}
      </Paper>

      {m.attendees_recognized.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>{tt('attendees', '出席者')}</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {m.attendees_recognized.map((n, i) => <Chip key={i} size="small" label={n} />)}</Stack>
        </Paper>
      )}

      <Accordion variant="outlined" className="no-print" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">{tt('transcript', '逐字稿')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.full_transcript || tt('none', '（無）')}</Typography>
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary">
        {tt('cost', '本次成本')}：{m.total_input_tokens + m.total_output_tokens} tokens · NT${(m.total_cost_ntd || 0).toFixed(2)}</Typography>
      <style>{'@media print{.no-print{display:none!important}}'}</style>
    </Box>
  );
};

export default MinutesPage;
